
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useNotificationSound } from './useNotificationSound';
import { useBrowserNotification } from './useBrowserNotification';
import { hapticFeedback } from '@/utils/mobileUtils';
import { useConsolidatedNotifications } from '@/contexts/ConsolidatedNotificationContext';

interface VisualNotificationOptions {
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  duration?: number;
  persistent?: boolean;
  progress?: number;
  showUndo?: boolean;
  showRetry?: boolean;
  playSound?: boolean;
  showBrowser?: boolean;
  haptic?: boolean;
  onUndo?: () => void;
  onRetry?: () => void;
}

export const useVisualNotifications = () => {
  const { toast } = useToast();
  const { hasPermission } = useConsolidatedNotifications();
  const { playNotificationSound } = useNotificationSound();
  const { showBrowserNotification } = useBrowserNotification();

  const showNotification = useCallback((options: VisualNotificationOptions) => {
    const {
      title,
      message,
      type,
      priority,
      duration = 5000,
      persistent = false,
      progress,
      showUndo = false,
      showRetry = false,
      playSound = true,
      showBrowser = false,
      haptic = true,
      onUndo,
      onRetry,
    } = options;

    // Enhanced notification displayed

    // Play haptic feedback for mobile
    if (haptic) {
      switch (priority) {
        case 'urgent':
          hapticFeedback('heavy');
          break;
        case 'high':
          hapticFeedback('medium');
          break;
        default:
          hapticFeedback('light');
          break;
      }
    }

    // Play sound notification
    if (playSound) {
      playNotificationSound(priority);
    }

    // Show browser notification (if tab is not active)
    if (showBrowser && document.hidden && hasPermission) {
      const now = new Date().toISOString();
      showBrowserNotification({
        id: `notification-${Date.now()}`,
        title,
        message,
        type: 'user_registered', // Use a valid notification type from the enum
        priority,
        created_at: now,
        updated_at: now,
        is_read: false,
        link: undefined,
      });
    }

    // Show toast notification
    const toastDuration = persistent ? undefined : duration;
    
    const toastConfig: any = {
      title,
      description: message,
      duration: toastDuration,
      variant: type === 'error' ? 'destructive' : 'default',
    };

    // Add action if undo or retry is needed
    if (showUndo && onUndo) {
      toastConfig.action = (
        <button
          onClick={onUndo}
          className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        >
          Undo
        </button>
      );
    } else if (showRetry && onRetry) {
      toastConfig.action = (
        <button
          onClick={onRetry}
          className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        >
          Retry
        </button>
      );
    }
    
    toast(toastConfig);
  }, [toast, playNotificationSound, showBrowserNotification, hasPermission]);

  const showSuccess = useCallback((title: string, message: string, options?: Partial<VisualNotificationOptions>) => {
    showNotification({
      title,
      message,
      type: 'success',
      priority: 'medium',
      ...options,
    });
  }, [showNotification]);

  const showError = useCallback((title: string, message: string, options?: Partial<VisualNotificationOptions>) => {
    showNotification({
      title,
      message,
      type: 'error',
      priority: 'high',
      ...options,
    });
  }, [showNotification]);

  const showWarning = useCallback((title: string, message: string, options?: Partial<VisualNotificationOptions>) => {
    showNotification({
      title,
      message,
      type: 'warning',
      priority: 'medium',
      ...options,
    });
  }, [showNotification]);

  const showInfo = useCallback((title: string, message: string, options?: Partial<VisualNotificationOptions>) => {
    showNotification({
      title,
      message,
      type: 'info',
      priority: 'low',
      ...options,
    });
  }, [showNotification]);

  const showLoading = useCallback((title: string, message: string, options?: Partial<VisualNotificationOptions>) => {
    showNotification({
      title,
      message,
      type: 'loading',
      priority: 'medium',
      persistent: true,
      ...options,
    });
  }, [showNotification]);

  return {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
  };
};
